// @ts-strict-ignore
import {Directive, Injector} from "@angular/core";
import {
	ApiServiceBase,
	SimClientStorageService,
	SimConfigurationService,
	SimInjectableDataService,
	SimLogService,
	SimUtilityService,
} from "@services";
import {
	HttpError,
	HubConnection,
	HubConnectionBuilder,
	HubConnectionState,
	ILogger,
	IRetryPolicy,
	JsonHubProtocol,
	LogLevel,
	RetryContext,
} from "@microsoft/signalr";
import {Translations} from "@translations";
import {GlobalEventEmitterService} from "@event-emitter";
import {DeviceActivityType} from "@models";

@Directive()
export abstract class BaseHubService {
	T = Translations;
	/*
    Debug only
     */
	public readonly Id: string;

	protected IsPlannedDisconnect = false;

	protected abstract readonly HubName: string;
	protected _emitterService = this._injector.get(GlobalEventEmitterService);
	protected _simLogService = this._injector.get(SimLogService);
	private _connection: HubConnection = null;
	private _simClientStorageService = this._injector.get(SimClientStorageService);
	private _simInjectableDataService = this._injector.get(SimInjectableDataService);

	private connectSystemPromise: Promise<{
		success: boolean;
		connectionId: string;
	}> = null;

	protected constructor(protected _injector: Injector) {
		this.Id = SimUtilityService.createGuid();
	}

	public get connectionId(): string | null {
		return this._connection?.connectionId;
	}

	public connect() {
		if (!this.connectSystemPromise?.["pending"]) {
			this.configureConnection();
			this.connectSystemPromise = this.startHub().then((result) => {
				this.connectSystemPromise["pending"] = false;
				this.setConnectionIdToGlobalStorage();
				return result;
			});

			this.connectSystemPromise["pending"] = true;
		}
		return this.connectSystemPromise;
	}

	public async disconnect() {
		if (this._connection) {
			this.IsPlannedDisconnect = true;
			await this._connection.stop();
			// stop triggers before the connection related logic in `OnConnectedAsync` is finished. So we need to wait, any pray :D
			await SimUtilityService.delay(1000);
		} else {
			return;
		}
	}

	public async reconnect() {
		await this.disconnect();
		this._connection = null;
		return await this.connect();
	}

	//Connection Status = Connected
	public isConnected() {
		if (this._connection) {
			return this._connection.state === HubConnectionState.Connected;
		}

		return false;
	}

	//Connection Status = Connecting, Connected, Reconnecting
	public isConnectionAlive() {
		let isAlive = false;

		if (this._connection) {
			isAlive =
				this._connection.state === HubConnectionState.Connecting ||
				this._connection.state === HubConnectionState.Connected ||
				this._connection.state === HubConnectionState.Reconnecting;
		}

		return isAlive;
	}

	public invoke<T = void>(methodName: string, ...args: any[]): Promise<T> {
		return new Promise<T>((resolve, reject) => {
			if (this.isConnected()) {
				this._connection.invoke(methodName, ...args).then(
					(ret) => {
						resolve(ret);
					},
					(error) => {
						console.error(`'${methodName}' error: ${error || ""} - args: ${args}`);
						reject(error);
					},
				);
			} else {
				//not connected
				console.warn(`'${methodName}' not called - reason: not connected!`);
				reject("not connected");
			}
		});
	}

	protected abstract wireOnMessage(connection: HubConnection): void;

	protected abstract connectionErrorAction(message: string): void;

	protected abstract connectionDisconnectedAction(): void;

	protected abstract connectionFirstConnectedAction(connectionId: string): void;

	protected abstract connectionReconnectedAction(connectionId: string): void;

	protected abstract addToGroup(topic: DeviceActivityType, topicId: string): void;

	protected abstract removeFromGroup(topic: DeviceActivityType, topicId: string): void;

	protected setConnectionIdToGlobalStorage() {
		this._simClientStorageService.setData(ApiServiceBase.WEBSOCKET_ID_KEY, this.connectionId);
	}

	private async startHub(): Promise<{
		success: boolean;
		connectionId: string;
	}> {
		if (this.isConnectionAlive()) {
			return {success: true, connectionId: this._connection.connectionId};
		}

		let success = await this._connection
			.start()
			.then(() => true)
			.catch((error) => {
				if (error instanceof HttpError) {
					console.warn(`${error?.statusCode}:${error?.message}`);
				} else {
					// Unknown generic error
					console.warn(JSON.stringify(error));
				}
				return false;
			});

		if (!success) {
			return {success: false, connectionId: null};
		}

		let connectionState = this._connection.state;
		switch (connectionState) {
			case HubConnectionState.Connected:
				this.connectionFirstConnectedAction(this._connection.connectionId);
				return {
					success: true,
					connectionId: this._connection.connectionId,
				};
			case HubConnectionState.Connecting:
				// See: SIMSOFT-2896
				// It can happen that the connection state is still "connecting"
				// at this point
				// Check until the connection is established
				let maxRetries = 10;
				while (maxRetries-- > 0) {
					await SimUtilityService.delay(100);
					if (this._connection.state === HubConnectionState.Connected) {
						this.connectionFirstConnectedAction(this._connection.connectionId);
						return {
							success: true,
							connectionId: this._connection.connectionId,
						};
					}
				}
				break;
		}

		return {success: false, connectionId: null};
	}

	private configureConnection() {
		if (!this._connection) {
			const queryParams = `?${ApiServiceBase.MAC_KEY}=${this._simInjectableDataService?.mac || ""}&${ApiServiceBase.DEVICE_ID_KEY}=${this._simInjectableDataService.clientData?.deviceId || ""}`;
			this._connection = new HubConnectionBuilder()
				.withUrl("/" + this.HubName + queryParams)
				.withHubProtocol(new JsonHubProtocol())
				.withAutomaticReconnect(new SimReconnectRetryPolicy())
				.configureLogging(
					new SimHubLogger(
						SimConfigurationService.settings.enableDebugMode,
						this._simLogService,
						this.connectionErrorAction.bind(this),
					),
				)
				.build();

			this._connection.keepAliveIntervalInMilliseconds = SimConfigurationService.settings.signalRKeepAliveTimeout * 1000;
			this._connection.serverTimeoutInMilliseconds = SimConfigurationService.settings.signalRDisconnectTimeout * 1000;

			this._connection.onclose((error) => {
				this.setConnectionIdToGlobalStorage();
				if (!this.IsPlannedDisconnect) {
					this.connectionDisconnectedAction();
				}
				this.IsPlannedDisconnect = false;
			});

			this._connection.onreconnected(() => {
				this.setConnectionIdToGlobalStorage();
				this.connectionReconnectedAction(this._connection.connectionId);
			});

			this.wireOnMessage(this._connection);
		}
	}
}

export class SimReconnectRetryPolicy implements IRetryPolicy {
	nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
		//first reconnect should be immediately (0ms), second after 1sec, every after that in 5sec intervals
		switch (retryContext.previousRetryCount) {
			case 0:
				return 0;
			case 1:
			case 2:
			case 3:
			case 4:
			case 5:
				return 1000;
			case 6:
			case 7:
			case 8:
			case 9:
			case 10:
				return 5000;
			default:
				return 10000;
		}
	}
}

export class SimHubLogger implements ILogger {
	constructor(
		private enableDebugMode: boolean,
		private _simLogService: SimLogService,
		private connectionErrorAction: (message: string) => void,
	) {}

	log(logLevel: LogLevel, message: string) {
		if (logLevel >= LogLevel.Warning || this.enableDebugMode) {
			if (logLevel >= LogLevel.Error) {
				//this._simLogService.logError({message: message});
				this.connectionErrorAction(message);
			} else {
				//this._simLogService.logInfo({message: message});
			}
		}
	}
}
